import api from './api';
import type {
  GrupoDocumento,
  DocumentoBiblioteca,
  VinculoEmpresaGrupo,
  CreateGrupoDocumentoDTO,
  CreateDocumentoDTO,
  CreateVinculoEmpresaDTO,
} from '../types';

// ============================================================================
// BIBLIOTECA DE DOCUMENTOS
// ============================================================================

export const bibliotecaDocumentosService = {
  // ========== GRUPOS DE DOCUMENTOS ==========
  
  /**
   * Lista todos os grupos de documentos
   * Admin: vê todos os grupos (ativos e inativos)
   * Usuário: vê apenas grupos autorizados para sua empresa
   */
  getAllGrupos: async (incluirInativos: boolean = true): Promise<GrupoDocumento[]> => {
    console.log('📚 [BibliotecaService] Buscando todos os grupos', { incluirInativos });
    const { data } = await api.get('/GruposDocumentos', {
      params: { incluirInativos }
    });
    console.log('✅ [BibliotecaService] Grupos recebidos:', data?.length || 0);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Obtém um grupo específico com documentos e empresas vinculadas
   */
  getGrupoById: async (id: string): Promise<GrupoDocumento> => {
    console.log('📚 [BibliotecaService] Buscando grupo:', id);
    const { data } = await api.get(`/GruposDocumentos/${id}`);
    console.log('✅ [BibliotecaService] Grupo recebido:', data?.nome);
    return data;
  },

  /**
   * Cria um novo grupo de documentos (admin only)
   */
  createGrupo: async (grupo: CreateGrupoDocumentoDTO): Promise<GrupoDocumento> => {
    console.log('📤 [BibliotecaService] Criando grupo:', grupo.nome);
    const { data } = await api.post('/GruposDocumentos', grupo);
    console.log('✅ [BibliotecaService] Grupo criado:', data?.id);
    return data;
  },

  /**
   * Atualiza um grupo existente (admin only)
   */
  updateGrupo: async (id: string, grupo: Partial<CreateGrupoDocumentoDTO>): Promise<GrupoDocumento> => {
    console.log('📤 [BibliotecaService] Atualizando grupo:', id);
    const { data } = await api.put(`/GruposDocumentos/${id}`, grupo);
    console.log('✅ [BibliotecaService] Grupo atualizado');
    return data;
  },

  /**
   * Deleta um grupo (admin only)
   * Soft delete se houver documentos vinculados
   */
  deleteGrupo: async (id: string): Promise<void> => {
    console.log('🗑️ [BibliotecaService] Deletando grupo:', id);
    await api.delete(`/GruposDocumentos/${id}`);
    console.log('✅ [BibliotecaService] Grupo deletado');
  },

  // ========== DOCUMENTOS ==========

  /**
   * Lista documentos de um grupo específico
   */
  getDocumentos: async (grupoId: string): Promise<DocumentoBiblioteca[]> => {
    console.log('📄 [BibliotecaService] Buscando documentos do grupo:', grupoId);
    const { data } = await api.get(`/GruposDocumentos/${grupoId}/Documentos`);
    console.log('✅ [BibliotecaService] Documentos recebidos:', data?.length || 0);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Cria um novo documento em um grupo (admin only)
   */
  createDocumento: async (grupoId: string, documento: CreateDocumentoDTO): Promise<DocumentoBiblioteca> => {
    console.log('📤 [BibliotecaService] Criando documento no grupo:', grupoId);
    console.log('📦 Dados do documento:', documento);
    const { data } = await api.post(`/GruposDocumentos/${grupoId}/Documentos`, documento);
    console.log('✅ [BibliotecaService] Documento criado:', data?.id);
    return data;
  },

  /**
   * Atualiza um documento existente (admin only)
   */
  updateDocumento: async (
    grupoId: string,
    documentoId: string,
    documento: Partial<CreateDocumentoDTO>
  ): Promise<DocumentoBiblioteca> => {
    console.log('📤 [BibliotecaService] Atualizando documento:', documentoId);
    const { data } = await api.put(
      `/GruposDocumentos/${grupoId}/Documentos/${documentoId}`,
      documento
    );
    console.log('✅ [BibliotecaService] Documento atualizado');
    return data;
  },

  /**
   * Deleta um documento (admin only)
   */
  deleteDocumento: async (grupoId: string, documentoId: string): Promise<void> => {
    console.log('🗑️ [BibliotecaService] Deletando documento:', documentoId);
    await api.delete(`/GruposDocumentos/${grupoId}/Documentos/${documentoId}`);
    console.log('✅ [BibliotecaService] Documento deletado');
  },

  // ========== VÍNCULOS EMPRESA-GRUPO ==========

  /**
   * Lista empresas vinculadas a um grupo (admin only)
   */
  getEmpresasVinculadas: async (grupoId: string): Promise<VinculoEmpresaGrupo[]> => {
    console.log('🏢 [BibliotecaService] Buscando empresas vinculadas ao grupo:', grupoId);
    const { data } = await api.get(`/GruposDocumentos/${grupoId}/Empresas`);
    console.log('✅ [BibliotecaService] Empresas vinculadas:', data?.length || 0);
    return Array.isArray(data) ? data : [];
  },

  /**
   * Vincula uma empresa a um grupo (admin only)
   */
  vincularEmpresa: async (
    grupoId: string,
    vinculo: CreateVinculoEmpresaDTO
  ): Promise<VinculoEmpresaGrupo> => {
    console.log('📤 [BibliotecaService] Vinculando empresa ao grupo:', grupoId);
    console.log('📦 Dados do vínculo:', vinculo);
    const { data } = await api.post(`/GruposDocumentos/${grupoId}/Empresas`, vinculo);
    console.log('✅ [BibliotecaService] Empresa vinculada');
    return data;
  },

  /**
   * Desvincula uma empresa de um grupo (admin only)
   */
  desvincularEmpresa: async (grupoId: string, entityId: string): Promise<void> => {
    console.log('🗑️ [BibliotecaService] Desvinculando empresa:', entityId, 'do grupo:', grupoId);
    await api.delete(`/GruposDocumentos/${grupoId}/Empresas/${entityId}`);
    console.log('✅ [BibliotecaService] Empresa desvinculada');
  },
};
